import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Card, Button, Row, Col, Alert, Badge } from 'react-bootstrap';
import { FaPhone, FaPlus, FaMinus, FaCheck, FaInfoCircle } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { PHONE_ADDON, FEATURE_FLAGS } from '../constants';
import apiClient from '../services/apiClient';

const PhoneSlots = ({ show, onClose, onSlotsUpdate }) => {
  const { user } = useAuth();
  
  // Always call hooks first - conditional logic comes after
  const [currentSlots, setCurrentSlots] = useState(0);
  const [targetSlots, setTargetSlots] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [phoneData, setPhoneData] = useState(null);

  // Map account types to ensure compatibility
  const mapAccountType = (userAccountType) => {
    const actualAccountType = userAccountType || user?.account_type || user?.accountType;
    
    const typeMap = {
      'individual': 'solo',
      'Individual': 'solo',
      'Solo': 'solo',
      'solo': 'solo',
      'Solo Assistant': 'solo_assistant',
      'solo_assistant': 'solo_assistant',
      'Small Team': 'small_team',
      'small_team': 'small_team',
      'Medium Team': 'medium_team',
      'medium_team': 'medium_team',
      'Large Team': 'large_team',
      'large_team': 'large_team',
      'Brokerage': 'brokerage',
      'brokerage': 'brokerage',
      'broker': 'brokerage',
      'Super': 'super',
      'super': 'super'
    };
    
    const mappedType = typeMap[actualAccountType] || actualAccountType?.toLowerCase() || 'solo';
    return mappedType;
  };

  const accountType = mapAccountType(user?.account_type);
  const maxAllowedSlots = PHONE_ADDON.MAX_SLOTS_PER_ACCOUNT[accountType] || 3;
  const recommendedSlots = PHONE_ADDON.RECOMMENDED_SLOTS[accountType] || 1;
  const monthlyCostPerSlot = PHONE_ADDON.MONTHLY_COST_PER_SLOT;

  const loadPhoneData = useCallback(async () => {
    try {
      const userId = user?.id || user?.userId || user?.user_id;
      console.log('[DEBUG] PhoneSlots - Loading phone data from API for userId:', userId);
      console.log('[DEBUG] PhoneSlots - Full user object:', user);
      console.log('[DEBUG] PhoneSlots - Available user ID fields:', {
        id: user?.id,
        userId: user?.userId,
        user_id: user?.user_id
      });

      if (!userId) {
        console.error('[DEBUG] PhoneSlots - No user ID found!');
        console.error('[DEBUG] PhoneSlots - User must be authenticated to manage phone slots');
        setError('Please log in to manage phone slots');
        setCurrentSlots(0);
        setTargetSlots(0);
        return;
      }

      // Load from billing API endpoint instead of phoneSlots
      const response = await apiClient.get(`/api/billing/${userId}`);

      console.log('[DEBUG] Loaded billing data from API:', response.data);

      // Extract the actual data from the API response
      const billingUserData = response.data.success ? response.data.data : response.data;
      console.log('[DEBUG] Extracted billing user data:', billingUserData);

      if (billingUserData && typeof billingUserData.phoneSlots === 'number') {
        setPhoneData(billingUserData);
        setCurrentSlots(billingUserData.phoneSlots);
        setTargetSlots(billingUserData.phoneSlots);
        console.log('[DEBUG] Set currentSlots to:', billingUserData.phoneSlots);
      } else {
        console.log('[DEBUG] No phoneSlots found in billing data, initializing defaults');
        setPhoneData(null);
        setCurrentSlots(0);
        setTargetSlots(0);
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        console.log('[DEBUG] No billing data found for user, will create new data when needed');
        console.log('[DEBUG] User exists but has no billing data yet - this is normal for new users');
        setPhoneData(null);
        setCurrentSlots(0);
        setTargetSlots(0);
        setError(null);
        return;
      }
      console.error('Error loading phone data:', error);
      setCurrentSlots(0);
      setTargetSlots(0);
    }
  }, [user]);

  useEffect(() => {
    const userId = user?.id || user?.userId || user?.user_id;
    if (show && userId && FEATURE_FLAGS.PHONE_FEATURE_ENABLED) {
      loadPhoneData();
    }
  }, [show, user?.id, user?.userId, user?.user_id, loadPhoneData]);

  // If phone feature is disabled, don't render the component
  if (!FEATURE_FLAGS.PHONE_FEATURE_ENABLED) {
    return null;
  }

  const calculateMonthlyCost = (slots) => {
    return slots * monthlyCostPerSlot;
  };

  const handleUpdateSlots = async () => {
    if (targetSlots === currentSlots) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const userId = user?.id || user?.userId || user?.user_id;
      console.log(`[DEBUG] PhoneSlots - Updating phone slots from ${currentSlots} to ${targetSlots} for user ${userId}`);
      
      if (!userId) {
        throw new Error('No user ID found');
      }
      
      // Prepare billing update data
      const billingUpdate = {
        phoneSlots: targetSlots,
        phoneSlotsCost: targetSlots * monthlyCostPerSlot
      };
      
      console.log('[DEBUG] Sending updated billing data to API:', billingUpdate);
      
      // Save to billing API endpoint
      const response = await apiClient.put(`/api/billing/${userId}`, billingUpdate);
      
      console.log('[DEBUG] Billing data saved successfully:', response.data);
      
      const newMonthlyCost = calculateMonthlyCost(targetSlots);
      
      setCurrentSlots(targetSlots);
      setPhoneData({ phoneSlots: targetSlots });
      setSuccess({
        message: `Phone slots updated successfully! ${targetSlots > currentSlots ? 'Added' : 'Removed'} ${Math.abs(targetSlots - currentSlots)} slot(s).`,
        newMonthlyCost
      });
      
      // Notify parent component
      if (onSlotsUpdate) {
        onSlotsUpdate(targetSlots);
      }
      
      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose();
        setSuccess(null);
      }, 2000);
      
    } catch (error) {
      console.error('Error updating phone slots:', error);
      setError('Failed to update phone slots. Please try again.');
    }
    
    setLoading(false);
  };

  const formatAccountTypeForDisplay = (type) => {
    const displayMap = {
      'solo': 'Solo',
      'solo_assistant': 'Solo Assistant',
      'small_team': 'Small Team',
      'medium_team': 'Medium Team',
      'large_team': 'Large Team',
      'brokerage': 'Brokerage',
      'super': 'Super'
    };
    
    return displayMap[type] || type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Solo';
  };

  const currentMonthlyCost = calculateMonthlyCost(currentSlots);
  const targetMonthlyCost = calculateMonthlyCost(targetSlots);
  const costDifference = targetMonthlyCost - currentMonthlyCost;

  return (
    <Modal 
      show={show} 
      onHide={onClose}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header style={{
        background: 'linear-gradient(180deg, #eef2f0 0%, #e4ece8 100%)',
        borderBottom: '1px solid #c8d8d2',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.07)',
        borderRadius: 'calc(0.3rem - 1px) calc(0.3rem - 1px) 0 0',
        margin: 0,
        padding: '0 20px',
        minHeight: 42,
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          {accountType && (
            <div style={{
              position: 'absolute', left: 20,
              background: '#dcfce7', color: '#15803d',
              padding: '3px 10px', borderRadius: 999,
              fontSize: 12, fontWeight: 600,
              border: '1px solid #bbf7d0',
              letterSpacing: '0.01em',
            }}>
              {formatAccountTypeForDisplay(accountType)} Plan
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 24, height: 24, borderRadius: 7,
              background: '#d1fae5', color: '#047857',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 900,
            }}>📞</div>
            <h2 style={{
              margin: 0, color: '#1e3a2f',
              fontSize: 18, fontWeight: 700,
              letterSpacing: '-0.01em',
            }}>Manage Phone Slots</h2>
          </div>
          <button
            type="button"
            aria-label="Close"
            style={{
              position: 'absolute',
              top: '50%',
              right: 12,
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              outline: 'none',
              padding: 0,
              fontSize: '1.4rem',
              color: '#4b6360',
              lineHeight: 1,
            }}
            onClick={onClose}
            onMouseOver={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.07)'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'none'; }}
          >×</button>
        </div>
      </Modal.Header>
      
      <Modal.Body style={{ padding: '20px 30px' }}>
        {/* Phone Slots Overview */}
        <Card className="mb-4" style={{ border: '1px solid #e3f2fd', background: '#f8fbff' }}>
          <Card.Body>
            <Row>
              <Col md={4}>
                <div className="text-center">
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#0d47a1' }}>
                    {currentSlots}
                  </div>
                  <div style={{ color: '#666', fontSize: '0.9rem' }}>Current Slots</div>
                </div>
              </Col>
              <Col md={4}>
                <div className="text-center">
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#2e7d32' }}>
                    ${calculateMonthlyCost(currentSlots).toFixed(2)}
                  </div>
                  <div style={{ color: '#666', fontSize: '0.9rem' }}>Total Monthly Cost</div>
                  <div style={{ color: '#888', fontSize: '0.8rem', marginTop: '4px' }}>
                    ${monthlyCostPerSlot.toFixed(2)} per slot
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div className="text-center">
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#ed6c02' }}>
                    {maxAllowedSlots}
                  </div>
                  <div style={{ color: '#666', fontSize: '0.9rem' }}>Max Allowed</div>
                </div>
              </Col>
            </Row>
            
            {/* Available Phone Numbers Display */}
            {phoneData && phoneData.phoneSlots && phoneData.phoneSlots.length > 0 && (
              <div style={{ marginTop: '20px', padding: '16px', background: '#f0f8f0', borderRadius: '8px', border: '1px solid #d4edda' }}>
                <h6 style={{ color: '#155724', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                  <FaPhone style={{ marginRight: '8px' }} />
                  Available Phone Numbers
                </h6>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {phoneData.phoneSlots.map((slot, index) => (
                    <div key={slot.id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      background: '#ffffff',
                      borderRadius: '6px',
                      border: '1px solid #c3e6cb'
                    }}>
                      <div>
                        <span style={{ fontWeight: '500', color: '#155724' }}>
                          {slot.phoneNumber}
                        </span>
                        <span style={{ marginLeft: '12px', color: '#6c757d', fontSize: '0.85rem' }}>
                          {slot.assignedTo}
                        </span>
                      </div>
                      <Badge 
                        bg={slot.status === 'active' ? 'success' : 'secondary'} 
                        style={{ fontSize: '0.75rem' }}
                      >
                        {slot.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Slot Adjustment */}
        <Card style={{ border: '1px solid #e0e6ed', borderRadius: '8px' }}>
          <Card.Body style={{ padding: '20px' }}>
            <h5 style={{ marginBottom: '20px', fontWeight: 600, color: '#333' }}>Adjust Phone Slots</h5>
            
            {/* Trial User Warning */}
            {(user?.accountType?.toLowerCase() === 'trial' || 
              user?.is_trial === 'true' || 
              user?.trial_status === 'active') && (
              <Alert variant="warning" className="mb-3">
                <FaInfoCircle className="me-2" />
                <strong>Trial Account Limitation:</strong> Phone slot changes unavailable on trial — upgrade to manage.
              </Alert>
            )}
            
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <label style={{ fontWeight: '500', color: '#555', marginBottom: '8px', display: 'block' }}>
                  Number of Phone Slots
                </label>
                <div style={{ fontSize: '0.9rem', color: '#777' }}>
                  Recommended for {formatAccountTypeForDisplay(accountType)}: {recommendedSlots} slot{recommendedSlots !== 1 ? 's' : ''}
                </div>
              </div>
              
              <div className="d-flex align-items-center" style={{ gap: '15px' }}>
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={(() => {
                    const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                       user?.is_trial === 'true' || 
                                       user?.trial_status === 'active';
                    return isTrialUser || targetSlots <= 0 || loading;
                  })()}
                  onClick={() => setTargetSlots(Math.max(0, targetSlots - 1))}
                  style={{ 
                    width: '40px', 
                    height: '40px', 
                    padding: 0,
                    opacity: (() => {
                      const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                         user?.is_trial === 'true' || 
                                         user?.trial_status === 'active';
                      return (isTrialUser || targetSlots <= 0 || loading) ? 0.5 : 1;
                    })()
                  }}
                  title={(() => {
                    const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                       user?.is_trial === 'true' || 
                                       user?.trial_status === 'active';
                    if (isTrialUser) return 'Phone slot adjustments not available during trial period';
                    if (targetSlots <= 0) return 'Cannot reduce slots below zero';
                    if (loading) return 'Processing...';
                    return 'Remove one phone slot';
                  })()}
                >
                  <FaMinus />
                </Button>
                
                <div style={{ 
                  minWidth: '60px', 
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: '#0d47a1'
                }}>
                  {targetSlots}
                </div>
                
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={(() => {
                    const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                       user?.is_trial === 'true' || 
                                       user?.trial_status === 'active';
                    return isTrialUser || targetSlots >= maxAllowedSlots || loading;
                  })()}
                  onClick={() => setTargetSlots(Math.min(maxAllowedSlots, targetSlots + 1))}
                  style={{ 
                    width: '40px', 
                    height: '40px', 
                    padding: 0,
                    opacity: (() => {
                      const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                         user?.is_trial === 'true' || 
                                         user?.trial_status === 'active';
                      return (isTrialUser || targetSlots >= maxAllowedSlots || loading) ? 0.5 : 1;
                    })()
                  }}
                  title={(() => {
                    const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                       user?.is_trial === 'true' || 
                                       user?.trial_status === 'active';
                    if (isTrialUser) return 'Phone slot adjustments not available during trial period';
                    if (targetSlots >= maxAllowedSlots) return 'Cannot exceed maximum allowed slots';
                    if (loading) return 'Processing...';
                    return 'Add one phone slot';
                  })()}
                >
                  <FaPlus />
                </Button>
              </div>
            </div>

            {/* Cost Breakdown */}
            {targetSlots !== currentSlots && (
              <div style={{ 
                background: '#f8f9fa', 
                padding: '15px', 
                borderRadius: '6px',
                marginBottom: '20px',
                border: '1px solid #e9ecef'
              }}>
                <h6 style={{ marginBottom: '12px', fontWeight: 600, color: '#333' }}>Cost Summary</h6>
                
                <div className="d-flex justify-content-between mb-2">
                  <span>Monthly Cost ({targetSlots} slots):</span>
                  <strong>${targetMonthlyCost.toFixed(2)}/month</strong>
                </div>
                
                <hr style={{ margin: '8px 0' }} />
                
                <div className="d-flex justify-content-between">
                  <span style={{ fontWeight: '600' }}>
                    Monthly Change:
                  </span>
                  <span style={{ 
                    fontWeight: 'bold', 
                    color: costDifference > 0 ? '#dc3545' : costDifference < 0 ? '#28a745' : '#6c757d'
                  }}>
                    {costDifference > 0 ? '+' : ''}${costDifference.toFixed(2)}/month
                  </span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="d-flex justify-content-end" style={{ gap: '12px' }}>
              <Button
                onClick={onClose}
                disabled={loading}
                style={{
                  background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                  border: 'none',
                  color: '#333',
                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                  transition: 'all 0.2s ease-in-out',
                  borderRadius: '4px',
                  fontWeight: '500',
                  padding: '8px 24px',
                  fontSize: '1rem',
                  letterSpacing: '0.5px'
                }}
              >
                Cancel
              </Button>
              
              <Button
                onClick={handleUpdateSlots}
                disabled={targetSlots === currentSlots || loading}
                style={{
                  background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                  border: 'none',
                  color: '#333',
                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                  transition: 'all 0.2s ease-in-out',
                  borderRadius: '4px',
                  fontWeight: '500',
                  padding: '8px 24px',
                  fontSize: '1rem',
                  letterSpacing: '0.5px',
                  minWidth: '120px'
                }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <FaCheck className="me-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Success/Error Messages */}
        {success && (
          <Alert variant="success" className="mt-3">
            <FaCheck className="me-2" />
            {success.message}
            <div className="mt-2">
              <small>New monthly cost: ${success.newMonthlyCost.toFixed(2)}</small>
            </div>
          </Alert>
        )}
        
        {error && (
          <Alert variant="danger" className="mt-3">
            <FaInfoCircle className="me-2" />
            {error}
          </Alert>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default PhoneSlots;
